import React from 'react';
import { type FileRejection } from 'react-dropzone';
import { DropzoneWrapper } from '@/components/elements/DropzoneWrapper';
import {
	CTAScreen,
	DropImage,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Button } from '@/components/UI';
import { useLandingShownEvent } from '@/hooks/useLandingShownEvent';
import { useSendToAmplitudeScrolledPercent } from '@/hooks/useSendToAmplitudeScrolledPercent';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { useUploadImage } from '@/hooks/useUploadImage';
import { AmplitudeService } from '@/services/amplitudeService';
import { CookiesStorageService } from '@/services/cookiesStorageService';
import { GenerateLandingLinkService } from '@/services/generateLandingLinkService';
import { GoogleService } from '@/services/googleService';
import {
	benefitsFilterLego,
	funnelReviewsFilterLego,
	howItWorkFilterLego,
	whyLiftFilterLego,
} from '@/data/ai-filter-lego';
import { reviewsLanding } from '@/data/reviewsData';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LANDING_CONSTANTS,
	LANDING_COOKIE_STORAGE,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import benefit from '@/public/images/ai-filters/lego/benefit.webp';
import funnelImg1 from '@/public/images/ai-filters/lego/funnelImg1.webp';
import funnelImg2 from '@/public/images/ai-filters/lego/funnelImg2.webp';
import howItWork from '@/public/images/ai-filters/lego/howWork.webp';
import whyLift from '@/public/images/ai-filters/lego/whyLift.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './LegoLanding.module.scss';

const LegoLanding = () => {
	// Translations
	const { t } = useTranslationWithLocale('ai-filters-lego');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews');

	// States
	const [loading, setLoading] = React.useState(false);

	// Logic
	const uploadImage = useUploadImage();

	// Helpers
	const isLoading = loading || uploadImage.isLoading;

	// Actions
	const handleDrop = (
		acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		if (fileRejections.length > 0) {
			console.warn('Rejected files:', fileRejections);
			return;
		}

		setLoading(true);
		uploadImage.mutate(
			{ file: acceptedFiles[0] },
			{
				onSuccess: async ({ viewUrl }) => {
					CookiesStorageService.set(
						LANDING_COOKIE_STORAGE.IMAGE_FROM_LANDING,
						viewUrl,
					);
					GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
					await AmplitudeService.logEventAsync(AMPLITUDE_EVENTS.MEDIA_ADDED, {
						context: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_LEGO_LANDING,
					});
					setLoading(false);

					window.location.href =
						GenerateLandingLinkService.funnelImageAiFilterLego();
				},
				onError: () => {
					setLoading(false);
				},
			},
		);
	};
	const openGalleryEvent = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.MEDIA_GALLERY_SHOWN, {
			context: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_LEGO_LANDING,
		});
	};

	// Analytics
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.AI_FILTERS_LEGO_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_LEGO },
	);
	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.AI_FILTERS_LEGO });
	return (
		<>
			<DropImage
				imageLeft={{
					src: funnelImg1,
					alt: 'filter preview',
				}}
				imageRight={{
					src: funnelImg2,
					alt: 'filter preview',
				}}
				t={t}
				isLoading={isLoading}
				dropzoneOptions={{
					disabled: isLoading,
					multiple: false,
					accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
					maxFiles: LANDING_CONSTANTS.MAX_FILES,
					maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
					onDrop: handleDrop,
					onFileDialogOpen: openGalleryEvent,
				}}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				reviewsData={funnelReviewsFilterLego}
				classes={{
					dropdownClass: styles['legoLanding__drop-image-container'],
				}}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				image={howItWork}
				data={howItWorkFilterLego}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['legoLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				image={benefit}
				specialListItem
				data={benefitsFilterLego}
				t={t}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['legoLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* WhyLift */}
			<ImageTextSection
				title={t('whyLift.mainTitle')}
				image={whyLift}
				data={whyLiftFilterLego}
				t={t}
				classes={{
					imageContainer: styles['legoLanding__whyLift-image-container'],
					list: styles['legoLanding__whyLift-list'],
				}}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['legoLanding__drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				title_green={t('tryLiftData.greenText')}
				title2={t('tryLiftData.title2')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonText={t('getStarted')}
				buttonNode={
					<DropzoneWrapper
						options={{
							multiple: false,
							accept: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE,
							maxFiles: LANDING_CONSTANTS.MAX_FILES,
							maxSize: LANDING_CONSTANTS.MAX_FILE_SIZE,
							onDrop: handleDrop,
							onFileDialogOpen: openGalleryEvent,
						}}
					>
						<Button
							as='button'
							preset='landing_outline'
							text={t('getStarted')}
							size='large'
							classes={{
								button: styles['legoLanding__cta-drop-wrapper-button'],
							}}
						/>
					</DropzoneWrapper>
				}
			/>
		</>
	);
};

export default LegoLanding;
